/* GlobalCommandBar.jsx — iter-182 · operational command layer.
 *
 * Premium compact search trigger that opens the canonical
 * CommandPalette (⌘K). Per the iter-182 reference:
 *   • top-left of the content area, aligned with the workspace grid
 *   • desktop: compact rounded field (~300px) with search icon,
 *     contextual placeholder, and ⌘K kbd hint
 *   • mobile: icon-only button that opens the same overlay
 *   • contextual placeholders adapt by route
 *   • calm hover (soft elevation), calm focus (subtle violet glow)
 *
 * The bar is purely a TRIGGER — it doesn't own search state. It
 * dispatches the existing `cf:open-search` event on the global
 * cfBus so the palette opens with full keyboard navigation. The
 * ⌘K global listener also remains live (set by CommandPalette).
 */
import React, { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { Search } from "lucide-react";
import { useAuth } from "../../data/authStore";
import { cfBus } from "../../data/chainStore";

// Hide on public + auth + landing pages.
const PUBLIC_PATHS = new Set([
  "/", "/pricing", "/login", "/signup", "/forgot-password",
  "/reset-password", "/accept-invite",
]);

// Hide on routes that own their own top chrome / dark theme so the
// command bar doesn't clash visually. iter-201D.A — `/dashboard`
// (Today) is no longer suppressed; the global rail now provides
// the canonical top search across every app route, while pages
// keep any in-page search fields as separate surfaces.
const SUPPRESSED_EXACT = new Set([
]);

// Mobile-only suppression — preserves the desktop floating pill on
// these routes (the desktop pill still adds calm value) but hides
// the mobile floating ball that would otherwise sit on top of the
// route's bespoke search field.
const SUPPRESSED_MOBILE_EXACT = new Set([
  "/deal-rooms",
]);

/* Detect whether the current path lives inside a single deal-room
 * detail page. We still allow the global bar there since the brief
 * lists Deal Rooms as a supported context. */
export const isAppRoute = (path) => {
  if (PUBLIC_PATHS.has(path)) return false;
  if (SUPPRESSED_EXACT.has(path)) return false;
  return true;
};

/* Contextual placeholders per the iter-182 brief. */
const placeholderFor = (path) => {
  if (path.startsWith("/documents"))   return "Search documents, contracts, forms…";
  if (path.startsWith("/properties"))  return "Search properties, sellers, listings…";
  if (path.startsWith("/contacts"))    return "Search contacts, solicitors, brokers…";
  if (path.startsWith("/chains"))      return "Search chains, parties, blockers…";
  if (path.startsWith("/insights"))    return "Search intelligence, risks, signals…";
  if (path.startsWith("/today"))       return "Search updates, blockers, exchanges…";
  if (path.startsWith("/sales-progression")) return "Search deals, updates, blockers…";
  if (path.startsWith("/deal-rooms"))  return "Search deals, properties, people…";
  return "Search deals, properties, people, documents…";
};

/* ⌘ on macOS, Ctrl elsewhere. SSR-safe. */
const useShortcutBadge = () => {
  return useMemo(() => {
    if (typeof navigator === "undefined") return "⌘";
    const ua = navigator.userAgent || "";
    return /Mac|iPhone|iPad|iPod/i.test(ua) ? "⌘" : "Ctrl";
  }, []);
};

/* GlobalCommandBarButton — iter-201D.A
 *
 * The pill button only. Has no positioning of its own; it's hosted
 * inside the TopCommandRail at xl+ widths. Smaller, calmer,
 * infrastructural — width 280px, height 32px (was 320 × 36) to
 * reduce dominance against page titles.
 */
export const GlobalCommandBarButton = () => {
  const { pathname } = useLocation();
  const shortcut = useShortcutBadge();
  const open = () => cfBus.emit("cf:open-search", null);
  const onKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " " || e.key === "/") {
      e.preventDefault();
      open();
    }
  };
  const placeholder = placeholderFor(pathname);

  return (
    <button
      type="button"
      onClick={open}
      onKeyDown={onKeyDown}
      aria-label="Open command bar"
      className="group flex items-center gap-2.5 h-8 pl-3 pr-2 rounded-full text-left transition-all duration-200 ease-out cf-press"
      style={{
        width: 280,
        background: "rgba(255,255,255,0.96)",
        backdropFilter: "blur(14px) saturate(140%)",
        WebkitBackdropFilter: "blur(14px) saturate(140%)",
        border: "1px solid rgba(15,23,42,0.08)",
        boxShadow:
          "0 1px 2px rgba(15,23,42,0.05), 0 10px 26px -14px rgba(15,23,42,0.20)",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow =
          "0 2px 4px rgba(15,23,42,0.06), 0 14px 30px -14px rgba(15,23,42,0.25)";
        e.currentTarget.style.borderColor = "rgba(15,23,42,0.12)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow =
          "0 1px 2px rgba(15,23,42,0.05), 0 10px 26px -14px rgba(15,23,42,0.20)";
        e.currentTarget.style.borderColor = "rgba(15,23,42,0.08)";
      }}
      onFocus={(e) => {
        e.currentTarget.style.boxShadow =
          "0 1px 2px rgba(15,23,42,0.05), 0 0 0 3px rgba(139,92,246,0.22)";
        e.currentTarget.style.borderColor = "rgba(139,92,246,0.45)";
      }}
      onBlur={(e) => {
        e.currentTarget.style.boxShadow =
          "0 1px 2px rgba(15,23,42,0.05), 0 10px 26px -14px rgba(15,23,42,0.20)";
        e.currentTarget.style.borderColor = "rgba(15,23,42,0.08)";
      }}
      data-testid="global-command-bar-trigger"
    >
      <Search size={13.5} className="text-slate-400 shrink-0" strokeWidth={2} />
      <span
        className="flex-1 truncate text-[12.5px] text-slate-500/90 font-medium"
        data-testid="global-command-bar-placeholder"
      >
        {placeholder}
      </span>
      <span
        className="inline-flex items-center gap-0.5 shrink-0 text-[10px] font-semibold tabular-nums px-1.5 py-0.5 rounded-md"
        style={{
          background: "rgba(15,23,42,0.05)",
          color: "rgba(15,23,42,0.55)",
          border: "1px solid rgba(15,23,42,0.06)",
        }}
        aria-label={`Shortcut: ${shortcut} K`}
      >
        <span className="text-[11px] leading-none">{shortcut}</span>
        <span>K</span>
      </span>
    </button>
  );
};

/* GlobalCommandBar — legacy export. iter-201D.A retires the
 * fixed-overlay variant; the canonical mount lives inside
 * <TopCommandRail /> via <GlobalCommandBarButton />. Kept as a
 * no-op so any stale imports degrade silently without crashing. */
export const GlobalCommandBar = () => null;

/* ─── Mobile icon-only trigger ────────────────────────────────────
 *  Compact circular button per the brief — opens the same palette
 *  via cf:open-search. Lives in the bottom-right of the viewport
 *  above the bottom nav (thumb-reachable). Never a permanent
 *  full-width search bar on mobile.
 */
export const GlobalCommandBarMobile = () => {
  const { user } = useAuth();
  const { pathname } = useLocation();
  if (!user) return null;
  if (!isAppRoute(pathname)) return null;
  if (SUPPRESSED_MOBILE_EXACT.has(pathname)) return null;
  const open = () => cfBus.emit("cf:open-search", null);
  return (
    <button
      type="button"
      onClick={open}
      aria-label="Open command bar"
      className="xl:hidden fixed z-30 inline-flex items-center justify-center"
      style={{
        // Sits above the mobile bottom nav (88px) — thumb-reachable.
        bottom: "calc(env(safe-area-inset-bottom, 0px) + 96px)",
        right: 16,
        width: 44,
        height: 44,
        borderRadius: 999,
        background: "linear-gradient(135deg,#7C3AED,#6366F1)",
        boxShadow: "0 10px 22px -8px rgba(124,58,237,0.55), 0 1px 2px rgba(15,23,42,0.06)",
        color: "#fff",
      }}
      data-testid="global-command-bar-mobile"
    >
      <Search size={17} strokeWidth={2.2} />
    </button>
  );
};

export default GlobalCommandBar;
